import { useState, useEffect } from 'react';
import { supabase } from '@/utils/supabase';
import { createComponentLogger } from '@/utils/logger';

const log = createComponentLogger('useAvailableMilestones', 'encompass-prospects');

export interface MilestoneCount {
  milestone: string;
  count: number;
  displayOrder: number;
}

/**
 * Fetches available milestones from the milestone templates table.
 * Only returns milestones that are configured in a template (Brokered or Banked-Retail).
 * Includes loan counts for each milestone, sorted by template display order.
 */
export function useAvailableMilestones(organizationId: string | undefined) {
  const [milestones, setMilestones] = useState<MilestoneCount[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!organizationId) {
      setMilestones([]);
      setIsLoading(false);
      return;
    }

    const fetchMilestones = async () => {
      setIsLoading(true);
      try {
        // Step 1: Get valid milestones from templates (in display order)
        const { data: validMilestones, error: templateError } = await supabase
          .rpc('get_valid_milestone_names', { p_organization_id: organizationId });

        if (templateError) {
          log.error('Failed to fetch milestone templates', { organizationId }, templateError);
          return;
        }

        if (!validMilestones || validMilestones.length === 0) {
          log.warn('No milestone templates found', { organizationId });
          setMilestones([]);
          return;
        }

        // Create a map of milestone name -> display order
        const milestoneOrderMap = new Map<string, number>();
        for (const m of validMilestones) {
          milestoneOrderMap.set(m.milestone_name, m.min_display_order);
        }

        // Step 2: Get loan counts for each milestone
        const { data: loanData, error: loanError } = await supabase
          .from('encompass_loans')
          .select('milestone')
          .eq('organization_id', organizationId)
          .eq('deleted_from_encompass', false)
          .neq('loan_folder', 'Transferred Data')
          .not('milestone', 'is', null);

        if (loanError) {
          log.error('Failed to fetch loan milestones', { organizationId }, loanError);
          return;
        }

        // Count by milestone (only for valid milestones)
        const milestoneCounts = new Map<string, number>();
        for (const row of loanData || []) {
          const milestone = row.milestone;
          if (milestone && milestoneOrderMap.has(milestone)) {
            milestoneCounts.set(milestone, (milestoneCounts.get(milestone) || 0) + 1);
          }
        }

        // Build result array with all valid milestones (even if count is 0)
        const result: MilestoneCount[] = validMilestones.map((m: { milestone_name: string; min_display_order: number }) => ({
          milestone: m.milestone_name,
          count: milestoneCounts.get(m.milestone_name) || 0,
          displayOrder: m.min_display_order
        }));

        // Sort by display order
        result.sort((a, b) => a.displayOrder - b.displayOrder);

        log.info('Loaded milestones from templates', {
          organizationId,
          validCount: validMilestones.length,
          withLoans: result.filter(m => m.count > 0).length
        });

        setMilestones(result);
      } catch (err) {
        log.error('Error fetching milestones', { organizationId }, err as Error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMilestones();
  }, [organizationId]);

  return { milestones, isLoading };
}
